import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import DirectoryRowCard from "../components/DirectoryRowCard";
import {
  saasCircleCountSx,
  saasDirectoryWrapSx,
  saasHeroActionButtonSx,
  saasInputSx,
  saasSectionCardSx,
  saasSoftCountSx,
  saasTabsSx,
} from "../components/saasStyles";

const initialForm = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  whatsappNumber: "",
  email: "",
  notes: "",
};

const initialEditForm = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  whatsappNumber: "",
  email: "",
  notes: "",
  active: true,
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function TechniciansPage() {
  const [searchParams] = useSearchParams();
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [editForm, setEditForm] = useState(initialEditForm);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [quickAction, setQuickAction] = useState("default");
  const [selectedTechnicianId, setSelectedTechnicianId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState("orders");

  const loadTechnicians = async () => {
    const response = await api.get("/api/technicians");
    setTechnicians(response.data);
  };

  const loadTechnicianDetail = async (id) => {
    const response = await api.get(`/api/technicians/${id}`);
    setSelectedTechnician(response.data);
    setEditForm({
      firstName: response.data.first_name || "",
      lastName: response.data.last_name || "",
      phoneNumber: response.data.phone_number || "",
      whatsappNumber: response.data.whatsapp_number || "",
      email: response.data.email || "",
      notes: response.data.notes || "",
      active: Boolean(response.data.active),
    });
  };

  useEffect(() => {
    loadTechnicians().catch(() => setMessage({ type: "error", text: "Les techniciens n'ont pas pu etre charges." }));
  }, []);

  useEffect(() => {
    const technicianId = searchParams.get("technicianId");
    if (technicianId) {
      setSelectedTechnicianId(Number(technicianId));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selectedTechnicianId) {
      setSelectedTechnician(null);
      setEditForm(initialEditForm);
      return;
    }

    loadTechnicianDetail(selectedTechnicianId).catch(() =>
      setMessage({ type: "error", text: "Les details du technicien n'ont pas pu etre charges." })
    );
  }, [selectedTechnicianId]);

  const filteredTechnicians = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = !needle
      ? technicians
      : technicians.filter((technician) =>
          [
            technician.first_name,
            technician.last_name,
            technician.phone_number,
            technician.whatsapp_number,
            technician.email,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle)
        );

    if (quickAction === "with_whatsapp") {
      return base.filter((technician) => technician.whatsapp_number);
    }
    if (quickAction === "active") {
      return base.filter((technician) => technician.active);
    }
    return base;
  }, [technicians, search, quickAction]);

  const handleSubmit = async () => {
    try {
      await api.post("/api/technicians", form);
      setForm(initialForm);
      await loadTechnicians();
      setCreateOpen(false);
      setMessage({ type: "success", text: "Le technicien a ete cree." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le technicien n'a pas pu etre enregistre." });
    }
  };

  const handleSave = async () => {
    if (!selectedTechnicianId) {
      return;
    }

    try {
      const response = await api.patch(`/api/technicians/${selectedTechnicianId}`, editForm);
      await loadTechnicians();
      await loadTechnicianDetail(selectedTechnicianId);
      setMessage({ type: "success", text: "Le technicien a ete mis a jour." });
      setSelectedTechnician((current) => ({ ...current, ...response.data }));
    } catch (_error) {
      setMessage({ type: "error", text: "Le technicien n'a pas pu etre enregistre." });
    }
  };

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Repertoire des techniciens"
      description="Vue des techniciens, de leurs disponibilites et de leurs actions."
      stats={[]}
      action={
        <Button
          variant="contained"
          startIcon={<AddCircleRoundedIcon />}
          onClick={() => setCreateOpen(true)}
          sx={saasHeroActionButtonSx}
        >
          + Nouveau technicien
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box
        sx={{
          ...saasDirectoryWrapSx,
          borderRadius: "44px",
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.2, md: 1.4 },
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          Afficher {filteredTechnicians.length} elements
        </Typography>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} alignItems={{ xs: "stretch", xl: "center" }}>
          <TextField
            label="Rechercher un technicien"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              ...saasInputSx,
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "18px",
                minHeight: 44,
                backgroundColor: "#fff",
                boxShadow: "none",
              },
            }}
            InputProps={{
              startAdornment: <SearchRoundedIcon sx={{ color: "#5f6fff", mr: 1 }} />,
            }}
          />

          <FormControl sx={{ minWidth: { xs: "100%", xl: 350 } }}>
            <Select
              value={quickAction}
              onChange={(event) => setQuickAction(event.target.value)}
              displayEmpty
              sx={{ minHeight: 56, borderRadius: "18px", bgcolor: "#fff", fontWeight: 700, boxShadow: "none" }}
            >
              <MenuItem value="default">Action rapide</MenuItem>
              <MenuItem value="active">Actifs seulement</MenuItem>
              <MenuItem value="with_whatsapp">Avec WhatsApp</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ...saasSoftCountSx, minWidth: 106, minHeight: 44, bgcolor: "#f5f7ff" }}>
            <Typography variant="body2" sx={{ color: "#5867ff", fontWeight: 700 }}>
              {filteredTechnicians.length} techniciens
            </Typography>
          </Box>

          <Box
            sx={{
              width: 58,
              height: 44,
              borderRadius: "16px",
              border: "1px solid #dfe6f4",
              bgcolor: "#fff",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Badge badgeContent={technicians.filter((technician) => technician.active).length} color="error">
              <IconButton size="small" sx={{ color: "#5a68ff" }}>
                <NotificationsRoundedIcon />
              </IconButton>
            </Badge>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.1fr 0.9fr" }, gap: 3 }}>
        <Box
          sx={{
            ...saasSectionCardSx,
            borderRadius: "44px",
            display: "grid",
            gap: 2.25,
            px: { xs: 2, md: 2.4 },
            py: { xs: 1.4, md: 1.8 },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>
                Techniciens
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vue structuree avec contact, disponibilite WhatsApp et contexte equipe.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25}>
              <Box sx={saasCircleCountSx}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Visibles
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                    {filteredTechnicians.length}
                  </Typography>
                </Box>
              </Box>
              <Box sx={saasCircleCountSx}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Selection
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                    {selectedTechnicianId ? 1 : 0}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          {filteredTechnicians.map((technician) => (
            <DirectoryRowCard
              key={technician.id}
              selected={selectedTechnicianId === technician.id}
              avatarText={`${technician.first_name?.[0] || ""}${technician.last_name?.[0] || ""}`}
              title={`${technician.first_name} ${technician.last_name}`}
              subtitle={`TECH-${technician.id}`}
              chips={[
                { label: technician.active ? "Actif" : "Inactif", color: technician.active ? "success" : "default" },
                { label: technician.whatsapp_number ? "WhatsApp" : "Telephone seulement", color: "default" },
              ]}
              columns={[
                {
                  label: "Contact",
                  value: technician.phone_number || "-",
                  secondary: technician.email || "Aucun e-mail",
                },
                {
                  label: "Disponibilite",
                  value: technician.whatsapp_number || "-",
                  secondary: technician.notes || "Aucune note",
                },
              ]}
              actionLabel={selectedTechnicianId === technician.id ? "Selectionne" : "Choisir"}
              onSelect={() => setSelectedTechnicianId(technician.id)}
            />
          ))}
        </Box>

        <Box
          sx={{
            ...saasSectionCardSx,
            borderRadius: "44px",
            display: "grid",
            gap: 2,
            px: { xs: 2, md: 2.4 },
            py: { xs: 1.4, md: 1.8 },
            alignContent: "start",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>
              Espace technicien
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestion des contacts, du statut, des commandes et des rendez-vous.
            </Typography>
          </Box>

          {!selectedTechnician ? (
            <Typography variant="body2" color="text.secondary">
              Choisissez un technicien a gauche pour le modifier.
            </Typography>
          ) : (
            <>
              <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                <Box sx={{ ...saasSoftCountSx, minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Ouvert
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>
                    {selectedTechnician.metrics?.openOrders || 0} commandes
                  </Typography>
                </Box>
                <Box sx={{ ...saasSoftCountSx, minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Termine
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>
                    {selectedTechnician.metrics?.completedOrders || 0} commandes
                  </Typography>
                </Box>
                <Box sx={{ ...saasSoftCountSx, minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                    Rendez-vous
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>
                    {selectedTechnician.metrics?.appointmentCount || 0}
                  </Typography>
                </Box>
              </Stack>

              <TextField label="Prenom" value={editForm.firstName} onChange={(e) => setEditForm((c) => ({ ...c, firstName: e.target.value }))} sx={saasInputSx} />
              <TextField label="Nom" value={editForm.lastName} onChange={(e) => setEditForm((c) => ({ ...c, lastName: e.target.value }))} sx={saasInputSx} />
              <TextField label="Telephone" value={editForm.phoneNumber} onChange={(e) => setEditForm((c) => ({ ...c, phoneNumber: e.target.value }))} sx={saasInputSx} />
              <TextField label="WhatsApp" value={editForm.whatsappNumber} onChange={(e) => setEditForm((c) => ({ ...c, whatsappNumber: e.target.value }))} sx={saasInputSx} />
              <TextField label="E-mail" value={editForm.email} onChange={(e) => setEditForm((c) => ({ ...c, email: e.target.value }))} sx={saasInputSx} />
              <TextField label="Note" multiline minRows={3} value={editForm.notes} onChange={(e) => setEditForm((c) => ({ ...c, notes: e.target.value }))} sx={saasInputSx} />

              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 700 }}>
                  Actif
                </Typography>
                <Switch checked={editForm.active} onChange={(e) => setEditForm((c) => ({ ...c, active: e.target.checked }))} />
              </Stack>

              <Button variant="contained" onClick={handleSave} sx={{ borderRadius: "14px" }}>
                Enregistrer le technicien
              </Button>

              <Tabs value={workspaceTab} onChange={(_event, value) => setWorkspaceTab(value)} sx={saasTabsSx}>
                <Tab label="Commandes" value="orders" />
                <Tab label="Rendez-vous" value="appointments" />
              </Tabs>

              {workspaceTab === "orders" ? (
                <Box sx={{ display: "grid", gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800 }}>
                    Commandes affectees
                  </Typography>
                  {(selectedTechnician.assignments || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Aucune affectation pour le moment.
                    </Typography>
                  ) : (
                    (selectedTechnician.assignments || []).map((assignment) => (
                      <Box key={assignment.id} sx={{ border: "1px solid #e1e7f2", borderRadius: "16px", p: 1.25, bgcolor: "#fdfefe" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#25324b" }}>
                          Commande #{assignment.order_id} - {assignment.contact_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {assignment.order_status} / {assignment.status}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          Option A: {assignment.option_a_start ? `${formatDateTime(assignment.option_a_start)} - ${formatDateTime(assignment.option_a_end)}` : "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          Option B: {assignment.option_b_start ? `${formatDateTime(assignment.option_b_start)} - ${formatDateTime(assignment.option_b_end)}` : "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          Confirme: {assignment.confirmed_start ? `${formatDateTime(assignment.confirmed_start)} - ${formatDateTime(assignment.confirmed_end)}` : "-"}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "grid", gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800 }}>
                    Rendez-vous
                  </Typography>
                  {(selectedTechnician.appointments || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Aucun rendez-vous pour le moment.
                    </Typography>
                  ) : (
                    (selectedTechnician.appointments || []).map((appointment) => (
                      <Box key={appointment.id} sx={{ border: "1px solid #e1e7f2", borderRadius: "16px", p: 1.25, bgcolor: "#fdfefe" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#25324b" }}>
                          Commande #{appointment.order_id} - {appointment.contact_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          Statut: {appointment.status} / Commande: {appointment.order_status}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          Option A: {appointment.option_a_start ? `${formatDateTime(appointment.option_a_start)} - ${formatDateTime(appointment.option_a_end)}` : "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          Option B: {appointment.option_b_start ? `${formatDateTime(appointment.option_b_start)} - ${formatDateTime(appointment.option_b_end)}` : "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          Confirme: {appointment.confirmed_start ? `${formatDateTime(appointment.confirmed_start)} - ${formatDateTime(appointment.confirmed_end)}` : "-"}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Creer un technicien</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1.5 }}>
          <TextField label="Prenom" value={form.firstName} onChange={(e) => setForm((c) => ({ ...c, firstName: e.target.value }))} sx={saasInputSx} />
          <TextField label="Nom" value={form.lastName} onChange={(e) => setForm((c) => ({ ...c, lastName: e.target.value }))} sx={saasInputSx} />
          <TextField label="Telephone" value={form.phoneNumber} onChange={(e) => setForm((c) => ({ ...c, phoneNumber: e.target.value }))} sx={saasInputSx} />
          <TextField label="WhatsApp" value={form.whatsappNumber} onChange={(e) => setForm((c) => ({ ...c, whatsappNumber: e.target.value }))} sx={saasInputSx} />
          <TextField label="E-mail" value={form.email} onChange={(e) => setForm((c) => ({ ...c, email: e.target.value }))} sx={saasInputSx} />
          <TextField label="Note" multiline minRows={3} value={form.notes} onChange={(e) => setForm((c) => ({ ...c, notes: e.target.value }))} sx={saasInputSx} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!form.firstName || !form.lastName}>
            Enregistrer le technicien
          </Button>
        </DialogActions>
      </Dialog>
    </PageFrame>
  );
}
